import React, { useMemo, useRef, useState } from 'react';

/**
 * Interactive time-series chart — inline SVG, no charting dependency.
 *
 * Unlike a sparkline this carries the things needed to actually read a value:
 * a labelled y-axis, time ticks, gridlines, and a crosshair that reports the
 * value and timestamp under the pointer.
 *
 * Nulls are gaps, never zeros: a machine that went quiet is drawn as a break
 * in the line, so an outage cannot be mistaken for a reading of zero.
 */

interface Props {
  timestamps: string[];
  values: (number | null)[];
  color?: string;
  unit?: string;
  /** Pin the y-axis to 0–100 for percentages. */
  percent?: boolean;
  /** Bars suit per-interval counter deltas; lines suit gauges. */
  bars?: boolean;
  height?: number;
  /**
   * Hover index driven from outside, so every chart in a stack shows the same
   * instant. Correlating metrics is the whole point of stacking them.
   */
  hoverIndex?: number | null;
  onHoverIndexChange?: (index: number | null) => void;
  /** Drag-to-zoom: [start, end] indices, owned by the parent. */
  zoom?: [number, number] | null;
  onZoomChange?: (range: [number, number] | null) => void;
  /**
   * Called on a genuine click (not the tail of a zoom drag). The chart owns
   * this distinction because onMouseUp fires before onClick, so a parent
   * onClick would otherwise fire at the end of every drag.
   */
  onExpand?: () => void;
  /** Shows the "click to expand · drag to zoom" hint. */
  expandable?: boolean;
}

const PAD = { top: 10, right: 12, bottom: 22, left: 44 };

/**
 * Round axis bounds outward to a readable step.
 *
 * `nonNegative` keeps counts off a negative axis: an all-zero series would
 * otherwise render as -1..1 with a flat line through the middle and gridlines
 * for values the metric cannot have.
 */
const niceBounds = (min: number, max: number, nonNegative = false) => {
  if (min === max) {
    if (nonNegative) {
      // Flat series: show 0..value (or 0..1 when the value is 0 itself).
      return { lo: 0, hi: max > 0 ? max * 1.2 : 1 };
    }
    const pad = Math.abs(min) * 0.1 || 1;
    return { lo: min - pad, hi: max + pad };
  }
  const span = max - min;
  const step = Math.pow(10, Math.floor(Math.log10(span))) / 2;
  const lo = Math.floor(min / step) * step;
  return {
    lo: nonNegative ? Math.max(0, lo) : lo,
    hi: Math.ceil(max / step) * step,
  };
};

const fmtValue = (v: number) =>
  Math.abs(v) >= 1000 ? v.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : Number.isInteger(v) ? String(v)
      : v.toFixed(1);

export const TimeSeriesChart: React.FC<Props> = ({
  timestamps,
  values,
  color = '#2dd4bf',
  unit = '',
  percent = false,
  bars = false,
  height = 150,
  hoverIndex,
  onHoverIndexChange,
  zoom = null,
  onZoomChange,
  onExpand,
  expandable = false,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [localHover, setLocalHover] = useState<number | null>(null);
  // Drag selection in progress, as [anchor, current] indices.
  const [drag, setDrag] = useState<[number, number] | null>(null);

  // The parent owns hover when it wants charts synced.
  const synced = onHoverIndexChange !== undefined;
  const hover = synced ? (hoverIndex ?? null) : localHover;
  const setHover = (i: number | null) =>
    synced ? onHoverIndexChange?.(i) : setLocalHover(i);
  const gradientId = useMemo(() => `ts-${Math.random().toString(36).slice(2, 9)}`, []);

  // A mouseup outside the svg must still end the drag, otherwise the
  // selection sticks and the next hover keeps extending it.
  React.useEffect(() => {
    if (!drag) return;
    const finish = () => setDrag(null);
    window.addEventListener('mouseup', finish);
    return () => window.removeEventListener('mouseup', finish);
  }, [drag]);

  // Track the container so the chart fills whatever space it is given.
  React.useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  // Zoom is a window over the data; slice once and let the rest work on it.
  const [z0, z1] = zoom
    ? [Math.max(0, zoom[0]), Math.min(values.length - 1, zoom[1])]
    : [0, values.length - 1];
  const view = useMemo(() => values.slice(z0, z1 + 1), [values, z0, z1]);
  const viewTimes = useMemo(() => timestamps.slice(z0, z1 + 1), [timestamps, z0, z1]);

  const numeric = view.filter((v): v is number => typeof v === 'number');

  const { lo, hi } = useMemo(() => {
    if (percent) return { lo: 0, hi: 100 };
    if (numeric.length === 0) return { lo: 0, hi: 1 };
    const min = Math.min(...numeric);
    const max = Math.max(...numeric);
    // A series with no negative readings is a count, so keep its axis at 0+.
    const nonNegative = min >= 0;
    return niceBounds(bars || nonNegative ? Math.min(0, min) : min, max, nonNegative);
  }, [numeric, percent, bars]);

  const plotW = Math.max(10, width - PAD.left - PAD.right);
  const plotH = height - PAD.top - PAD.bottom;

  const x = (i: number) =>
    PAD.left + (view.length <= 1 ? plotW / 2 : (i / (view.length - 1)) * plotW);
  const y = (v: number) => PAD.top + plotH - ((v - lo) / (hi - lo || 1)) * plotH;

  // Runs of consecutive readings, so gaps break the line.
  const runs = useMemo(() => {
    const out: { i: number; v: number }[][] = [];
    let cur: { i: number; v: number }[] = [];
    view.forEach((v, i) => {
      if (typeof v === 'number') cur.push({ i, v });
      else if (cur.length) { out.push(cur); cur = []; }
    });
    if (cur.length) out.push(cur);
    return out;
  }, [view]);

  const yTicks = useMemo(() => {
    const n = 4;
    const ticks = Array.from({ length: n + 1 }, (_, k) => lo + ((hi - lo) * k) / n);
    // Small integer ranges (0..1, 0..3) read badly as 0, 0.25, 0.5 ...
    const wholeOnly = hi - lo <= n && Number.isInteger(lo) && Number.isInteger(hi);
    return wholeOnly ? Array.from(new Set(ticks.map(Math.round))) : ticks;
  }, [lo, hi]);

  // Time ticks: a handful of evenly spaced labels.
  const xTicks = useMemo(() => {
    if (viewTimes.length === 0) return [];
    const want = Math.max(2, Math.min(8, Math.floor(plotW / 100)));
    const stride = Math.max(1, Math.floor((viewTimes.length - 1) / (want - 1)));
    const idx: number[] = [];
    for (let i = 0; i < viewTimes.length; i += stride) idx.push(i);
    if (idx[idx.length - 1] !== viewTimes.length - 1) idx.push(viewTimes.length - 1);
    return idx;
  }, [viewTimes, plotW]);

  // Span decides whether a tick needs a date as well as a time.
  const multiDay = useMemo(() => {
    if (viewTimes.length < 2) return false;
    const ms = +new Date(viewTimes[viewTimes.length - 1]) - +new Date(viewTimes[0]);
    return ms > 36 * 3600 * 1000;
  }, [viewTimes]);

  const tickLabel = (iso: string) => {
    const d = new Date(iso);
    return multiDay
      ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  /** Pointer x -> index within the visible slice. */
  const indexAt = (clientX: number, el: SVGSVGElement) => {
    const rect = el.getBoundingClientRect();
    const px = clientX - rect.left;
    if (view.length === 0) return null;
    const ratio = (px - PAD.left) / plotW;
    return Math.max(0, Math.min(view.length - 1, Math.round(ratio * (view.length - 1))));
  };

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const i = indexAt(e.clientX, e.currentTarget);
    if (i === null) return;
    // Report the absolute index so synced charts agree while zoomed.
    setHover(z0 + i);
    if (drag) setDrag([drag[0], i]);
  };

  const onDown = (e: React.MouseEvent<SVGSVGElement>) => {
    const i = indexAt(e.clientX, e.currentTarget);
    if (i !== null) setDrag([i, i]);
  };

  const onUp = () => {
    if (!drag) return;
    const [a, b] = drag;
    const [from, to] = a <= b ? [a, b] : [b, a];
    setDrag(null);

    // A short drag is a click, not a zoom: expand instead.
    if (to - from < 2) {
      onExpand?.();
      return;
    }
    onZoomChange?.([z0 + from, z0 + to]);
  };

  if (numeric.length === 0) {
    return (
      <div
        ref={wrapRef}
        style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center',
                 fontSize: 12, color: 'rgba(255,255,255,0.3)' }}
      >
        No readings in this window
      </div>
    );
  }

  // hover is an absolute index (so synced charts agree); drawing needs it
  // relative to the visible slice, and out-of-window hovers simply don't draw.
  const hoverLocal =
    hover !== null && hover >= z0 && hover <= z1 ? hover - z0 : null;
  const hoverValue = hoverLocal !== null ? view[hoverLocal] : null;
  const barWidth = Math.max(1, plotW / view.length - 1);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <svg
        width={width}
        height={height}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        onMouseDown={onDown}
        onMouseUp={onUp}
        style={{ display: 'block', cursor: drag ? 'ew-resize' : 'crosshair', userSelect: 'none' }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.3} />
            <stop offset="100%" stopColor={color} stopOpacity={0.02} />
          </linearGradient>
        </defs>

        {/* gridlines + y labels */}
        {yTicks.map((t, i) => (
          <g key={i}>
            <line
              x1={PAD.left} x2={PAD.left + plotW} y1={y(t)} y2={y(t)}
              stroke="rgba(255,255,255,0.06)" strokeWidth={1}
            />
            <text
              x={PAD.left - 6} y={y(t) + 3} textAnchor="end"
              fontSize={10} fill="rgba(255,255,255,0.35)"
            >
              {fmtValue(t)}
            </text>
          </g>
        ))}

        {/* time labels */}
        {xTicks.map((i) => (
          <text
            key={i}
            x={x(i)} y={height - 6}
            textAnchor={i === 0 ? 'start' : i === view.length - 1 ? 'end' : 'middle'}
            fontSize={10} fill="rgba(255,255,255,0.35)"
          >
            {tickLabel(viewTimes[i])}
          </text>
        ))}

        {/* series */}
        {bars
          ? view.map((v, i) =>
              typeof v === 'number' ? (
                <rect
                  key={i}
                  x={x(i) - barWidth / 2}
                  y={y(v)}
                  width={barWidth}
                  height={Math.max(1, y(lo) - y(v))}
                  fill={color}
                  opacity={hover === i ? 1 : 0.7}
                  rx={1}
                />
              ) : null,
            )
          : runs.map((pts, ri) => {
              const line = pts.map((p) => `${x(p.i)},${y(p.v)}`).join(' ');
              return (
                <g key={ri}>
                  {pts.length > 1 && (
                    <polygon
                      points={`${x(pts[0].i)},${y(lo)} ${line} ${x(pts[pts.length - 1].i)},${y(lo)}`}
                      fill={`url(#${gradientId})`}
                    />
                  )}
                  {pts.length > 1 ? (
                    <polyline
                      points={line} fill="none" stroke={color}
                      strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round"
                    />
                  ) : (
                    <circle cx={x(pts[0].i)} cy={y(pts[0].v)} r={2.5} fill={color} />
                  )}
                </g>
              );
            })}

        {/* drag-to-zoom selection */}
        {drag && Math.abs(drag[1] - drag[0]) >= 1 && (
          <rect
            x={x(Math.min(drag[0], drag[1]))}
            y={PAD.top}
            width={Math.abs(x(drag[1]) - x(drag[0]))}
            height={plotH}
            fill="rgba(255,255,255,0.12)"
            stroke="rgba(255,255,255,0.3)"
            strokeWidth={1}
          />
        )}

        {/* crosshair */}
        {hoverLocal !== null && !drag && (
          <g>
            <line
              x1={x(hoverLocal)} x2={x(hoverLocal)} y1={PAD.top} y2={PAD.top + plotH}
              stroke="rgba(255,255,255,0.25)" strokeWidth={1} strokeDasharray="3 3"
            />
            {typeof hoverValue === 'number' && !bars && (
              <circle
                cx={x(hoverLocal)} cy={y(hoverValue)} r={3.5}
                fill={color} stroke="#141414" strokeWidth={1.5}
              />
            )}
          </g>
        )}
      </svg>

      {/* readout */}
      {hoverLocal !== null && !drag && (
        <div
          style={{
            position: 'absolute',
            top: 2,
            left: Math.min(Math.max(x(hoverLocal) - 60, 0), Math.max(width - 130, 0)),
            pointerEvents: 'none',
            backgroundColor: 'rgba(10,10,10,0.94)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 8,
            padding: '6px 9px',
            minWidth: 118,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: '#ffffff' }}>
            {typeof hoverValue === 'number' ? `${fmtValue(hoverValue)}${unit}` : 'No reading'}
          </div>
          <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.5)', marginTop: 1 }}>
            {new Date(viewTimes[hoverLocal]).toLocaleString(undefined, {
              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
            })}
          </div>
        </div>
      )}

      {zoom && onZoomChange && (
        <button
          onClick={(e) => { e.stopPropagation(); onZoomChange(null); }}
          style={{
            position: 'absolute', top: 2, right: 2, fontSize: 10,
            padding: '3px 7px', borderRadius: 6, cursor: 'pointer',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)',
          }}
        >
          Reset zoom
        </button>
      )}

      {/* Sits above the axis so it cannot collide with the last time label. */}
      {expandable && !zoom && (
        <div
          style={{
            position: 'absolute', top: 2, right: 4, fontSize: 9.5,
            color: 'rgba(255,255,255,0.22)', pointerEvents: 'none',
          }}
        >
          click to expand · drag to zoom
        </div>
      )}
    </div>
  );
};

/**
 * Status timeline with hover — one cell per reading, coloured by state.
 * Gaps are "no contact", which is itself the signal worth seeing.
 */
export const StatusTimeline: React.FC<{
  timestamps: string[];
  values: (string | null)[];
  height?: number;
  /** Shared hover index, so the strip tracks the charts above it. */
  hoverIndex?: number | null;
  onHoverIndexChange?: (index: number | null) => void;
  /** Same visible window as the charts, when they are zoomed. */
  zoom?: [number, number] | null;
}> = ({ timestamps, values, height = 28, hoverIndex, onHoverIndexChange, zoom = null }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [localHover, setLocalHover] = useState<number | null>(null);

  const synced = onHoverIndexChange !== undefined;
  const absHover = synced ? (hoverIndex ?? null) : localHover;
  const setHover = (i: number | null) =>
    synced ? onHoverIndexChange?.(i) : setLocalHover(i);

  React.useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  if (values.length === 0) return null;

  const [z0, z1] = zoom
    ? [Math.max(0, zoom[0]), Math.min(values.length - 1, zoom[1])]
    : [0, values.length - 1];
  const view = values.slice(z0, z1 + 1);
  const viewTimes = timestamps.slice(z0, z1 + 1);
  const cell = width / Math.max(1, view.length);
  // Shared hover is absolute; this strip draws in slice coordinates.
  const hover = absHover !== null && absHover >= z0 && absHover <= z1 ? absHover - z0 : null;

  const colorFor = (v: string | null) => {
    if (!v) return 'rgba(255,255,255,0.08)';
    const s = v.toLowerCase();
    if (['online', 'active', 'healthy', 'running', 'ok'].includes(s)) return '#0d9488';
    if (['degraded', 'warning'].includes(s)) return '#d97706';
    return '#dc2626';
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <svg
        width={width}
        height={height}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const i = Math.floor((e.clientX - rect.left) / cell);
          setHover(i >= 0 && i < view.length ? z0 + i : null);
        }}
        onMouseLeave={() => setHover(null)}
        style={{ display: 'block', cursor: 'crosshair' }}
      >
        {view.map((v, i) => (
          <rect
            key={i}
            x={i * cell} y={0}
            width={Math.max(cell - 0.5, 0.5)} height={height}
            fill={colorFor(v)}
            opacity={hover === null || hover === i ? 1 : 0.55}
            rx={1}
          />
        ))}
      </svg>

      {hover !== null && (
        <div
          style={{
            position: 'absolute',
            top: height + 4,
            left: Math.min(Math.max(hover * cell - 55, 0), Math.max(width - 130, 0)),
            pointerEvents: 'none',
            zIndex: 5,
            backgroundColor: 'rgba(10,10,10,0.94)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 8,
            padding: '6px 9px',
            minWidth: 118,
          }}
        >
          <div style={{ fontSize: 12.5, fontWeight: 600, color: '#ffffff' }}>
            {view[hover] || 'No contact'}
          </div>
          <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.5)', marginTop: 1 }}>
            {new Date(viewTimes[hover]).toLocaleString(undefined, {
              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
            })}
          </div>
        </div>
      )}
    </div>
  );
};
